import { ChangeEvent, useState, useEffect } from 'react'
import { Box, BoxProps, Text, Input, Flex, SearchIcon } from '@pancakeswap/uikit'
import styled from 'styled-components'
import { isAddress } from 'utils'
import { useTranslation } from '@pancakeswap/localization'
import CircleLoader from 'components/Loader/CircleLoader'

enum ResultStatus {
  NOT_VALID,
  FOUND,
  NOT_FOUND,
}

interface AddressInputSelectProps extends BoxProps {
  onValidAddress?: (value: string) => Promise<boolean>
  onAddressClick: (value: string) => void
}

const SubMenu = styled.div<{ isOpen: boolean }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.cardHeaderBg};
  border-radius: 0 0 8px 8px;
  left: 0;
  padding-bottom: 8px;
  padding-top: 16px;
  position: absolute;
  top: calc(100% - 2px);
  transition: transform 0.15s, opacity 0.15s;
  transform: scaleY(0);
  transform-origin: top;
  width: 100%;
  z-index: 99;
  box-shadow: 0px 20px 32px rgb(231 82 65 / 10%);
  /* border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: none; */

  ${({ isOpen }) =>
    isOpen &&
    `
    height: auto;
    opacity: 1;
    transform: scaleY(1);
  `}
`

const StyledSearchBox = styled(Flex)`
  border-radius: var(--Base-Unit-XS-8, 8px);
  border: 1px solid var(--Border, rgba(255, 255, 255, 0.1));
  background: var(--Nature-1, #191b2e);
  position: relative;
  border-radius: 8px;
  height: 48px;

  input {
    position: absolute !important;
    inset: 0;
    background: transparent;
    border: none;
    outline: none;
    border-radius: 12px;
    padding-left: 50px;
    height: 48px;
    font-size: 16px;
    overflow: hidden;
    text-overflow: ellipsis;
    color: rgba(255, 255, 255, 0.6);
    &::placeholder {
      color: #ffffff99;
    }
    &:focus {
      box-shadow: none !important;
    }
  }

  svg {
    position: absolute !important;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
  }
`

const AddressLink = styled(Text)`
  cursor: pointer;
  overflow-wrap: break-word;
  font-weight: bold;
  padding-left: 16px;
  padding-right: 16px;
`

const initialState = {
  isFetching: false,
  resultFound: ResultStatus.NOT_VALID,
  value: '',
}

const defaultValidAddressHandler = () => Promise.resolve(true)

const AddressInputSelect: React.FC<React.PropsWithChildren<AddressInputSelectProps>> = ({
  onValidAddress = defaultValidAddressHandler,
  onAddressClick,
  ...props
}) => {
  const [state, setState] = useState(initialState)
  const { t } = useTranslation()
  const { isFetching, resultFound, value } = state

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const { value: newValue } = evt.target
    setState((prevState) => ({
      ...prevState,
      value: newValue,
    }))
  }

  const handleClick = () => {
    setState(initialState)
    onAddressClick(state.value)
  }

  // When we have a valid address fetch the data
  useEffect(() => {
    const isValidAddress = isAddress(value) !== false

    const validAddressHandler = async () => {
      try {
        setState((prevState) => ({ ...prevState, isFetching: true }))
        const hasResults = await onValidAddress(value)

        setState((prevState) => ({
          ...prevState,
          isFetching: false,
          resultFound: hasResults ? ResultStatus.FOUND : ResultStatus.NOT_FOUND,
        }))
      } catch {
        setState((prevState) => ({ ...prevState, isFetching: false }))
      }
    }

    if (isValidAddress) {
      validAddressHandler()
    } else {
      setState((prevState) => ({ ...prevState, resultFound: ResultStatus.NOT_VALID }))
    }
  }, [value, onValidAddress, setState])

  return (
    <StyledSearchBox position="relative" {...props}>
      <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M10.4823 1.43994C5.98039 1.43994 2.32227 5.09807 2.32227 9.59994C2.32227 14.1018 5.98039 17.7599 10.4823 17.7599C12.2635 17.7599 13.9098 17.1862 15.2523 16.2149L21.5673 22.5149L22.9173 21.1649L16.6773 14.9099C17.9035 13.4812 18.6423 11.6268 18.6423 9.59994C18.6423 5.09807 14.9841 1.43994 10.4823 1.43994ZM10.4823 2.39994C14.4648 2.39994 17.6823 5.61744 17.6823 9.59994C17.6823 13.5824 14.4648 16.7999 10.4823 16.7999C6.49977 16.7999 3.28227 13.5824 3.28227 9.59994C3.28227 5.61744 6.49977 2.39994 10.4823 2.39994Z"
          fill="white"
        />
      </svg>

      {/* <SearchIcon width="20px" color="#FFFFFF99" /> */}
      <Input
        placeholder={t('Search %subject%', { subject: t('Address') })}
        value={state.value}
        onChange={handleChange}
        style={{ position: 'relative', zIndex: 16, paddingRight: '40px', color: '#FFFFFF99' }}
      />
      {isFetching && (
        <Box position="absolute" top="15px" right="38px" style={{ zIndex: 17 }}>
          <CircleLoader />
        </Box>
      )}
      {/* <SubMenu isOpen={resultFound !== ResultStatus.NOT_VALID}> */}
      {!isFetching && (
        <SubMenu isOpen={state.value !== ''}>
          {resultFound === ResultStatus.FOUND ? (
            <AddressLink onClick={handleClick}>{state.value}</AddressLink>
          ) : (
            <Text px="16px" fontWeight="bold">
              No results found.
            </Text>
          )}
        </SubMenu>
      )}
    </StyledSearchBox>
  )
}

export default AddressInputSelect
